import { Link, Navigate, Outlet } from "@tanstack/react-router";
import { Suspense } from "react";

import { useSessionQuery } from "../lib/authQueries";
import { useTenantRole } from "../lib/useTenantRole";

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-svh items-center justify-center text-sm text-muted-foreground">
      {children}
    </div>
  );
}

/**
 * The Breaks Studio's own shell, outside the sidebar layout: a signed-in platform admin sees the
 * studio, the panel designer or the demo full screen; anyone else is sent to sign in or told the
 * page does not exist. The pages are lazy chunks (they carry their own three.js), so the
 * `Suspense` here is what shows while one loads. Every studio stylesheet is scoped under
 * `.breaks-studio`, so nothing it sets reaches the dashboard after you navigate away.
 */
export function BreaksStudioGate() {
  const { data: session, isPending, isRefetching } = useSessionQuery();
  const { query, isPlatformAdmin } = useTenantRole();

  if (isPending || (isRefetching && !session)) return <Centered>Loading…</Centered>;
  if (!session) return <Navigate to="/login" />;
  if (query.isPending) return <Centered>Loading…</Centered>;
  if (!isPlatformAdmin) {
    return (
      <Centered>
        <p>
          Page not found —{" "}
          <Link to="/" className="text-primary underline">
            back to tenants
          </Link>
          .
        </p>
      </Centered>
    );
  }
  return (
    <div className="breaks-studio fixed inset-0 overflow-hidden">
      <Suspense fallback={<Centered>Loading the studio…</Centered>}>
        <Outlet />
      </Suspense>
    </div>
  );
}
